import React, { useEffect, useState, useRef } from "react";
import { Form, Button, Input, Typography, message, Spin, Tabs } from "antd";
import { Link, useNavigate } from "react-router-dom";
import {
  UserRegGet,
  UserLogin,
  User_forget_password,
  sendLoginOTP,
  verifyOTPAndLogin,
} from "../../Api/CoreApi";
import LoginHeader from "../../Home_Page_WLog/LoginHeader/LoginHeader";
import Footer from "../../Home_Page_WLog/Footer/Footer";
import {
  UserOutlined,
  LockOutlined,
  MailOutlined,
  SafetyCertificateOutlined,
  LoginOutlined,
  ArrowLeftOutlined,
  CheckCircleFilled,
  PhoneOutlined,
  ClockCircleOutlined,
  CrownOutlined,
} from "@ant-design/icons";
import "./Login.css";

const { Title, Text } = Typography;

function Login() {
  const id_navigate = localStorage.getItem("id_navigate");
  const navigate = useNavigate();
  const [forget, setForget] = useState(1);
  const [devref, setDevref] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [timer, setTimer] = useState(0);
  const [mobileNumber, setMobileNumber] = useState("");
  const [loginTab, setLoginTab] = useState("password");
  const timerRef = useRef(null);
  const [form] = Form.useForm();

  const get = async () => {
    try {
      const response1 = await UserRegGet();
      const dev_get = response1.filter((i) => i.role === "developer");
      const dev_id = dev_get.map((i) => i.refer);
      setDevref(parseInt(dev_id));
    } catch (err) {
      console.error("Error fetching dev ref:", err);
    }
  };

  // ✅ Helper - Get role name for message
  const getRoleName = (role) => {
    const r = (role || "").toLowerCase();
    if (r === "admin") return "Admin";
    if (r === "superadmin") return "Super Admin";
    if (r === "staff") return "Staff";
    if (r === "developer" || r === "dev") return "Developer";
    return "Non-User";
  };

  // ✅ Helper - Show unauthorized message
  const showRoleBlockedMessage = (role) => {
    const roleName = getRoleName(role);
    message.error({
      content: (
        <span>
          🚫 <strong>{roleName}</strong> account detected! This page is for users only.{" "}
          <a
            href="/Admin_Login"
            style={{
              color: "#ff0055",
              fontWeight: 700,
              textDecoration: "underline",
              marginLeft: 4,
            }}
          >
            Login here →
          </a>
        </span>
      ),
      duration: 6,
    });
  };

  // ===== PASSWORD LOGIN =====
  const submit = async (values) => {
    setIsLoading(true);
    try {
      const payload = {
        username: values.username.trim(),
        password: values.password,
      };
      console.log("📤 Login Request:", { username: payload.username });
      const response = await UserLogin(payload);

      console.log("═══════════════════════════════════");
      console.log("📥 FULL Login Response:", response);
      console.log("🎭 Role received:", response.role);
      console.log("═══════════════════════════════════");

      if (!response?.token?.access) {
        const errorMsg =
          response?.error ||
          response?.details?.non_field_errors?.[0] ||
          "Invalid Credentials";
        message.error(`❌ ${errorMsg}`);
        return;
      }

      // ✅ ROLE CHECK - Sirf USER allowed
      const role = (response.role || "").toLowerCase();

      if (role !== "user") {
        // ✅ Admin/Staff/Developer ko block karo
        showRoleBlockedMessage(response.role);
        return;
      }

      // ✅ Sirf USER hai to save karo
      localStorage.setItem("access_token", response.token.access);
      localStorage.setItem("refresh_token", response.token.refresh);
      localStorage.setItem("role", response.role);
      localStorage.setItem("user_id", response.user_id);
      localStorage.setItem("ref", response.refer || "");
      localStorage.setItem("username", response.username || values.username);

      // ✅ Purana cache clear
      localStorage.removeItem("user_gender");
      localStorage.removeItem("user_caste");
      localStorage.removeItem("cached_user_id");

      console.log("✅ User logged in successfully");

      // ✅ User redirect
      message.success("💖 Welcome!");
      const redirectPath = id_navigate
        ? `/User_Data/${id_navigate}`
        : "/dashboard";
      navigate(redirectPath);
    } catch (err) {
      console.error("❌ Login Error:", err);
      let errorMsg = "Login failed. Please try again.";
      if (err.response?.data?.error) errorMsg = err.response.data.error;
      else if (err.response?.data?.details?.non_field_errors)
        errorMsg = err.response.data.details.non_field_errors[0];
      message.error(`❌ ${errorMsg}`);
    } finally {
      setIsLoading(false);
    }
  };

  // ===== SEND OTP =====
  const sendOTP = async () => {
    const contact = mobileNumber;

    if (!contact || contact.length < 10) {
      message.error("Please enter a valid 10-digit mobile number");
      return;
    }

    setOtpLoading(true);
    try {
      const response = await sendLoginOTP(contact);

      if (response?.message) {
        setOtpSent(true);
        setTimer(60);
        message.success("✅ OTP sent to your mobile number!");

        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
          setTimer((prev) => {
            if (prev <= 1) {
              clearInterval(timerRef.current);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    } catch (error) {
      const errorMsg = error.message || "Failed to send OTP";

      if (errorMsg.includes("not registered")) {
        message.error(
          <span>
            ❌ This number is not registered.{" "}
            <a href="/User_Reg" style={{ color: "#ff0055", fontWeight: 600 }}>
              Sign up here
            </a>
          </span>,
          5
        );
      } else {
        message.error(`❌ ${errorMsg}`);
      }
    } finally {
      setOtpLoading(false);
    }
  };

  // ===== VERIFY OTP & LOGIN =====
  const verifyOTP = async () => {
    const otp = form.getFieldValue("otp");

    if (!otp || otp.length < 6) {
      message.error("Please enter a valid 6-digit OTP");
      return;
    }

    if (!mobileNumber) {
      message.error("Please send OTP first");
      return;
    }

    setOtpLoading(true);
    try {
      const response = await verifyOTPAndLogin(mobileNumber, otp);
      console.log("📥 OTP Verify Response:", response);

      if (!response?.token?.access) {
        message.error(response?.error || "Invalid OTP. Please try again.");
        return;
      }

      // ✅ ROLE CHECK - Sirf USER allowed
      const role = (response.role || "user").toLowerCase();

      if (role !== "user") {
        showRoleBlockedMessage(response.role);
        if (timerRef.current) clearInterval(timerRef.current);
        return;
      }

      // ✅ Sirf USER hai to save karo
      localStorage.setItem("access_token", response.token.access);
      localStorage.setItem("refresh_token", response.token.refresh);
      localStorage.setItem("role", response.role || "user");
      localStorage.setItem("user_id", response.user_id);
      localStorage.setItem("ref", response.refer || "");
      localStorage.setItem("username", response.username || "user");

      // ✅ Purana cache clear
      localStorage.removeItem("user_gender");
      localStorage.removeItem("user_caste");
      localStorage.removeItem("cached_user_id");

      if (timerRef.current) clearInterval(timerRef.current);

      // ✅ User redirect
      message.success("💖 Welcome!");
      const redirectPath = id_navigate
        ? `/User_Data/${id_navigate}`
        : "/dashboard";
      navigate(redirectPath);
    } catch (error) {
      console.error("❌ OTP Verify Error:", error);
      message.error(error.message || "Failed to verify OTP");
    } finally {
      setOtpLoading(false);
    }
  };

  const forget_password = async (values) => {
    setIsLoading(true);
    try {
      const response = await User_forget_password(values);
      if (response) {
        message.success("Check your mail 📩");
      }
    } catch (error) {
      message.error("Failed to send reset link");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  useEffect(() => {
    get();
  }, []);

  const isMobile = window.innerWidth <= 768;

  const btnStyle = {
    width: "100%",
    height: 54,
    border: "none",
    borderRadius: 999,
    background: "linear-gradient(135deg, #ff0055, #ff4d88, #ff80aa)",
    color: "#fff",
    fontWeight: 700,
    fontSize: 16,
    boxShadow: "0 10px 30px rgba(255,0,85,.28)",
  };

  return (
    <>
      <div>
        <LoginHeader />
        <div className="lg-page-wrapper">
          {/* Background Gradient Layer */}
          <div className="lg-bg-layer" />

          {/* Floating Emojis */}
          {["💖", "💍", "💕", "🌹", "💗", "💝", "🌸", "💌", "🎀", "❤️"].map(
            (e, i) => (
              <span
                key={i}
                className="lg-float-emoji"
                style={{
                  top: `${8 + ((i * 7) % 78)}%`,
                  left: `${5 + ((i * 9) % 85)}%`,
                  fontSize: `${18 + (i % 5) * 5}px`,
                  animationDelay: `${i * 0.35}s`,
                  animationDuration: `${3 + i * 0.4}s`,
                }}
              >
                {e}
              </span>
            )
          )}

          <Spin
            spinning={isLoading || otpLoading}
            tip="Please wait..."
            size="large"
          >
            <div className="lg-container">
              <div className="lg-anim lg-card">
                {/* ===== LEFT PANEL ===== */}
                <div className="lg-left-panel">
                  <div className="lg-left-overlay" />

                  <div className="lg-left-content">
                    <div className="lg-badge">
                      <SafetyCertificateOutlined /> Trusted Matrimony Platform
                    </div>

                    <h1 className="lg-title">
                      Welcome Back to <br />
                      <span className="lg-title-gradient">
                        Your Journey of Love
                      </span>
                    </h1>

                    <p className="lg-description">
                      Login to continue exploring verified profiles, meaningful
                      matches, and beautiful connections crafted just for you.
                    </p>

                    <div className="lg-features">
                      {[
                        "Verified & trusted profiles",
                        "Private and secure communication",
                        "Smart matching for better compatibility",
                      ].map((item, index) => (
                        <div key={index} className="lg-feature-item">
                          <CheckCircleFilled className="lg-feature-icon" />
                          {item}
                        </div>
                      ))}
                    </div>

                    <div className="lg-stats">
                      {[
                        { icon: "💑", text: "50K+ Members" },
                        { icon: "💍", text: "15K+ Matches" },
                        { icon: "⭐", text: "98% Happy" },
                      ].map((item, i) => (
                        <div key={i} className="lg-stat-badge">
                          <span className="lg-stat-icon">{item.icon}</span>
                          {item.text}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* ===== RIGHT PANEL ===== */}
                <div className="lg-right-panel">
                  <div className="lg-shimmer-bar" />

                  <div className="lg-form-header">
                    <div className="lg-icon-circle">
                      {forget === 1 ? (
                        <LoginOutlined className="lg-header-icon" />
                      ) : (
                        <MailOutlined className="lg-header-icon" />
                      )}
                    </div>
                    <Title level={3} className="lg-form-title">
                      {forget === 1
                        ? "Login to Your Account"
                        : "Forgot Password"}
                    </Title>
                    <Text className="lg-form-subtitle">
                      {forget === 1
                        ? "Choose your preferred login method"
                        : "Enter your registered email to receive reset link"}
                    </Text>
                  </div>

                  {/* ✅ USER ONLY NOTICE
                  {forget === 1 && (
                    <div
                      style={{
                        background: "linear-gradient(135deg, #fff0f3, #ffe4ec)",
                        border: "1px solid #ffd3df",
                        borderRadius: 12,
                        padding: "10px 14px",
                        marginBottom: 16,
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 10,
                          background: "linear-gradient(135deg, #ff0055, #ff4d88)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <UserOutlined
                          style={{ color: "#fff", fontSize: 14 }}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#c2185b",
                            lineHeight: 1.3,
                          }}
                        >
                          User Login Only
                        </div>
                        <div
                          style={{
                            fontSize: 10.5,
                            color: "#888",
                            marginTop: 1,
                          }}
                        >
                          Admin/Staff/Developer?{" "}
                          <Link
                            to="/Admin_Login"
                            style={{
                              color: "#ff0055",
                              fontWeight: 700,
                              textDecoration: "none",
                            }}
                          >
                            Login here →
                          </Link>
                        </div>
                      </div>
                    </div>
                  )} */}

                  <div className="lg-form-box">
                    {forget === 1 ? (
                      <>
                        <Tabs
                          activeKey={loginTab}
                          onChange={setLoginTab}
                          className="lg-tabs"
                          centered
                          items={[
                            {
                              key: "password",
                              label: (
                                <span>
                                  <UserOutlined className="lg-tab-icon" />{" "}
                                  Password
                                </span>
                              ),
                              children: (
                                <Form
                                  layout="vertical"
                                  onFinish={submit}
                                  className="lg-form"
                                >
                                  <Form.Item
                                    label="Username / Email / Mobile / Profile ID"  // ✅ CHANGE
                                    name="username"
                                    rules={[
                                      {
                                        required: true,
                                        message: "Please enter username, email, mobile or profile ID",
                                      },
                                    ]}
                                  >
                                    <Input
                                      prefix={<UserOutlined />}
                                      placeholder="Username / Email / Mobile / Profile ID"  // ✅ CHANGE
                                    />
                                  </Form.Item>
                                  <Form.Item
                                    label="Password"
                                    name="password"
                                    hasFeedback
                                    rules={[
                                      {
                                        required: true,
                                        message: "Please enter your password",
                                      },
                                    ]}
                                  >
                                    <Input.Password
                                      prefix={<LockOutlined />}
                                      placeholder="Enter your password"
                                    />
                                  </Form.Item>
                                  <Form.Item
                                    style={{ marginTop: 24, marginBottom: 0 }}
                                  >
                                    <Button
                                      htmlType="submit"
                                      className="lg-btn"
                                      style={btnStyle}
                                      block
                                    >
                                      <LoginOutlined
                                        style={{ marginRight: 6 }}
                                      />{" "}
                                      Login
                                    </Button>
                                  </Form.Item>
                                </Form>
                              ),
                            },
                            {
                              key: "otp",
                              label: (
                                <span>
                                  <PhoneOutlined className="lg-tab-icon" />{" "}
                                  Mobile OTP
                                </span>
                              ),
                              children: (
                                <Form
                                  form={form}
                                  layout="vertical"
                                  className="lg-form"
                                >
                                  <Form.Item
                                    label="Mobile Number"
                                    name="contact"
                                    rules={[
                                      {
                                        required: true,
                                        message:
                                          "Please enter your mobile number",
                                      },
                                      {
                                        pattern: /^[0-9]{10}$/,
                                        message:
                                          "Please enter a valid 10-digit mobile number",
                                      },
                                    ]}
                                  >
                                    <div className="lg-otp-input">
                                      <Input
                                        prefix={<PhoneOutlined />}
                                        placeholder="Enter 10-digit mobile number"
                                        maxLength={10}
                                        onChange={(e) =>
                                          setMobileNumber(e.target.value)
                                        }
                                        disabled={otpSent}
                                      />
                                      <Button
                                        className="lg-otp-btn"
                                        onClick={sendOTP}
                                        disabled={
                                          otpLoading || (otpSent && timer > 0)
                                        }
                                      >
                                        {otpLoading
                                          ? "Sending..."
                                          : timer > 0
                                            ? `${timer}s`
                                            : "Send OTP"}
                                      </Button>
                                    </div>
                                  </Form.Item>

                                  {otpSent && (
                                    <Form.Item
                                      label="Enter OTP"
                                      name="otp"
                                      rules={[
                                        {
                                          required: true,
                                          message: "Please enter OTP",
                                        },
                                        {
                                          pattern: /^[0-9]{6}$/,
                                          message:
                                            "Please enter a valid 6-digit OTP",
                                        },
                                      ]}
                                      extra={
                                        timer > 0
                                          ? `⏳ OTP expires in ${timer}s (Check console for OTP)`
                                          : "⏰ OTP expired. Please resend."
                                      }
                                    >
                                      <Input
                                        prefix={<ClockCircleOutlined />}
                                        placeholder="Enter 6-digit OTP"
                                        maxLength={6}
                                      />
                                    </Form.Item>
                                  )}

                                  {otpSent && (
                                    <Form.Item
                                      style={{
                                        marginTop: 16,
                                        marginBottom: 0,
                                      }}
                                    >
                                      <Button
                                        className="lg-btn"
                                        style={btnStyle}
                                        block
                                        onClick={verifyOTP}
                                        disabled={otpLoading}
                                      >
                                        {otpLoading ? (
                                          "Verifying..."
                                        ) : (
                                          <>
                                            <LoginOutlined
                                              style={{ marginRight: 6 }}
                                            />{" "}
                                            Verify & Login
                                          </>
                                        )}
                                      </Button>
                                    </Form.Item>
                                  )}
                                </Form>
                              ),
                            },
                          ]}
                        />
                      </>
                    ) : (
                      <Form
                        layout="vertical"
                        onFinish={forget_password}
                        className="lg-form"
                      >
                        <Form.Item
                          label="Email Address"
                          name="recipient"
                          rules={[
                            {
                              required: true,
                              message: "Please enter your email",
                            },
                          ]}
                        >
                          <Input
                            prefix={<MailOutlined />}
                            placeholder="Enter your Email"
                          />
                        </Form.Item>
                        <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
                          <Button
                            htmlType="submit"
                            className="lg-btn"
                            style={btnStyle}
                            block
                          >
                            <MailOutlined style={{ marginRight: 6 }} /> Send
                            Reset Link
                          </Button>
                        </Form.Item>
                      </Form>
                    )}
                  </div>

                  {forget === 1 ? (
                    <div className="lg-bottom-links">
                      <Link to="/User_Reg" className="lg-signup-link">
                        Don't have an account?
                      </Link>
                      <Text
                        className="lg-forgot-link"
                        onClick={() => setForget(2)}
                      >
                        Forgot password?
                      </Text>
                    </div>
                  ) : (
                    <div className="lg-back-login">
                      <Button
                        type="link"
                        onClick={() => setForget(1)}
                        className="lg-back-btn"
                      >
                        <ArrowLeftOutlined /> Back to Login
                      </Button>
                    </div>
                  )}



                  <div className="lg-trust-badges">
                    {["Secure", "Private", "Verified"].map((t) => (
                      <span key={t} className="lg-trust-badge">
                        <CheckCircleFilled /> {t}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Spin>
        </div>
        <Footer />
      </div>
    </>
  );
}

export default Login;